import { acClient } from '@/clients/ac-client.js';
import { backboneService } from '@/services/backbone.js';
import { toastService } from '@/services/toast.js';
import { HatElement } from '@/components/hat-element.js';

class HatAdminInvitations extends HatElement {
  private _invitations: any[] = [];
  private _nextPageToken: string | null = null;

  connectedCallback() {
    super.connectedCallback();
    this.renderShell();
    this.loadInvitations();
  }

  async loadInvitations(pageToken: string | null | undefined = null) {
    try {
      const { invitations, nextPageToken } = await acClient.getInvitations(pageToken);
      this._invitations = pageToken
        ? [...this._invitations, ...invitations]
        : invitations;
      this._nextPageToken = nextPageToken;
      this.renderContent();
    } catch {
      toastService.show('Failed to load invitations.');
    }
  }

  async deleteInvitation(id: string) {
    if (!confirm('Delete this invitation?')) return;
    try {
      await acClient.deleteInvitation(id);
      this._invitations = this._invitations.filter(i => i.invitationId !== id);
      this.renderContent();
      toastService.show('Invitation deleted.', 'success');
    } catch {
      toastService.show('Failed to delete invitation.');
    }
  }

  renderShell() {
    this.root.innerHTML = `
      <style>
        .toolbar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        h2 {
          color: white;
          font-size: 1.4rem;
          margin: 0;
          text-shadow: 0 2px 4px rgba(0,0,0,0.3);
        }

        .btn-primary {
          background: white;
          color: #764ba2;
          border: none;
          padding: 8px 16px;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          font-size: 0.9rem;
        }

        .btn-primary:hover { opacity: 0.9; }

        table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        th {
          background: #f8f8f8;
          text-align: left;
          padding: 12px 16px;
          font-size: 0.78rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #666;
          border-bottom: 1px solid #eee;
        }

        td {
          padding: 12px 16px;
          border-bottom: 1px solid #f0f0f0;
          font-size: 0.95rem;
        }

        tr:last-child td { border-bottom: none; }

        .inv-link {
          color: #764ba2;
          cursor: pointer;
          font-weight: 500;
          background: none;
          border: none;
          padding: 0;
          font-size: inherit;
          text-align: left;
        }

        .inv-link:hover { text-decoration: underline; }

        .status {
          display: inline-block;
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 0.8rem;
          font-weight: 600;
        }

        .status-pending  { background: #fef3c7; color: #92400e; }
        .status-redeemed { background: #d1fae5; color: #065f46; }
        .status-revoked  { background: #fee2e2; color: #991b1b; }

        .delete-btn {
          background: none;
          border: 1px solid #ddd;
          border-radius: 6px;
          padding: 4px 10px;
          cursor: pointer;
          color: #666;
          font-size: 0.85rem;
        }

        .delete-btn:hover { border-color: #c0392b; color: #c0392b; }

        .empty {
          text-align: center;
          padding: 40px;
          color: rgba(255,255,255,0.7);
        }

        .load-more {
          margin-top: 16px;
          text-align: center;
        }

        .btn-secondary {
          background: rgba(255,255,255,0.2);
          color: white;
          border: 1px solid rgba(255,255,255,0.4);
          padding: 8px 20px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 0.9rem;
        }

        .btn-secondary:disabled {
          opacity: 0.6;
          cursor: default;
        }

        .loading {
          text-align: center;
          padding: 40px;
          color: rgba(255,255,255,0.7);
        }
      </style>

      <div class="toolbar">
        <h2>Invitations</h2>
        <button class="btn-primary" id="new-btn" hat-entitlement="feature.admin.invitations.new">+ New Invitation</button>
      </div>
      <div id="content"><p class="loading">Loading…</p></div>
    `;

    this.root.getElementById('new-btn')!.addEventListener('click', () => {
      backboneService.navigateTo('/admin/invitations/new');
    });

    this._applyEntitlements();
  }

  renderContent() {
    const content = this.root.getElementById('content');
    if (!content) return;

    if (this._invitations.length === 0) {
      content.innerHTML = `<p class="empty">No invitations yet.</p>`;
      return;
    }

    const rows = this._invitations.map(inv => `
      <tr>
        <td><button class="inv-link" data-id="${inv.invitationId}">${inv.email}</button></td>
        <td>${inv.type}</td>
        <td><span class="status status-${inv.status.toLowerCase()}">${inv.status}</span></td>
        <td>${new Date(inv.createdTS).toLocaleDateString()}</td>
        <td><button class="delete-btn" data-id="${inv.invitationId}" hat-entitlement="feature.admin.invitations.delete">Delete</button></td>
      </tr>
    `).join('');

    content.innerHTML = `
      <table>
        <thead>
          <tr>
            <th>Email</th><th>Type</th><th>Status</th><th>Created</th><th></th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
      ${this._nextPageToken
        ? `<div class="load-more"><button class="btn-secondary" id="load-more-btn">Load more</button></div>`
        : ''}
    `;

    content.querySelectorAll<HTMLElement>('.inv-link').forEach(btn => {
      btn.addEventListener('click', () => {
        backboneService.navigateTo(`/admin/invitations/${btn.dataset.id}`);
      });
    });

    content.querySelectorAll<HTMLElement>('.delete-btn').forEach(btn => {
      btn.addEventListener('click', () => this.deleteInvitation(btn.dataset.id!));
    });

    content.querySelector('#load-more-btn')?.addEventListener('click', (e) => {
      const btn = e.currentTarget as HTMLButtonElement;
      btn.disabled = true;
      btn.textContent = 'Loading…';
      void this.loadInvitations(this._nextPageToken ?? undefined);
    });

    this._applyEntitlements();
  }
}

customElements.define('hat-admin-invitations', HatAdminInvitations);